import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import store from 'store';
import { Flex } from 'rebass';
import { useDispatch } from 'react-redux';
import MemberOnboardLoading from 'members/complete-profile/onboarding-loading';
import { useGetMemberSWR } from '../members/api';
import { setAuth } from './actions';
import CompleteProfile from '../members/complete-profile';
import Loading from '../app/loading/index';
import { useEntity } from '../entities/hooks/index';
import { useCurrentMemberId } from '../members/hooks';
import { useCreateNewAccount } from './hooks';
import { useMyChildJournals, useGiftCertificates } from 'account-dashboard/hooks';
import { checkIfPublicRoute } from 'app/form/helper';
import LoginSignUp from 'babys-first-year/LoginSignup';
import { getAuthToken } from './api';
import Cookies from 'js-cookie';
import { get, patch } from 'utils/api';
import useHistory from 'app/hooks/history';
import ProcessingScreen from './ProcessingScreen';
import ErrorScreen from './ErrorScreen';
import { isSelfClaimedGift } from 'members/helper';

export function getOrderId() {
  let orderId = Cookies.get('order_id');
  if (!orderId) {
    const queryParams = new URLSearchParams(window.location.search);
    orderId = queryParams.get('orderId');
  }
  return orderId;
}

// ✅ Check immediately at module level — no waiting for renders
function isTellAStoryRoute() {
  return window.location.pathname.includes('/tell_a_story');
}

function hasRestoreToken() {
  return (
    window.location.search.includes('restoreTokenId') &&
    window.location.search.includes('restoreTokenSecret')
  );
}

const AuthMember = ({ children }) => {
  const { push } = useHistory();
  const dispatch = useDispatch();
  const createNewUser = useCreateNewAccount();
  const { pathname } = useLocation();

  const [isProcessing, setIsProcessing] = useState(false);
  const [hasError, setHasError] = useState(false);

  // ✅ Track if we already bootstrapped to prevent double calls
  const hasBootstrapped = useRef(false);

  const isPublicRoute = checkIfPublicRoute(pathname);

  const { data, error, revalidate } = useGetMemberSWR('me', {
    revalidateOnFocus: false,
    shouldRetryOnError: false,
  });

  const currentMemberId = useCurrentMemberId();
  // `member` defaults to `{}` before it loads (useEntity never returns undefined),
  // so "have we actually got the member yet" has to come from `isPresent`, not
  // truthiness of `member` itself.
  const { member, isPresent: isMemberLoaded } = useEntity({
    type: 'members',
    id: currentMemberId || '',
  });

  // Same "which tab" logic as MeDropdown (src/app/navigation/links/me-dropdown.js)
  // — kept in sync with it so a user who lands with an orderId already logged in
  // gets sent to the same tab the nav would send them to.
  const { journals, isLoading: isJournalsLoading } = useMyChildJournals();
  const { giftCertificates, isLoading: isGiftCertificatesLoading } = useGiftCertificates();
  const getAccountRedirect = () => {
    const giftedJournalIds = new Set(
      giftCertificates.filter(gift => !isSelfClaimedGift(gift))
        .map(({ childJournalId }) => childJournalId).filter(Boolean)
    );
    const validJournals = journals.filter(({ id }) => !giftedJournalIds.has(id));
    const unassignedJournal = validJournals.find(({ state }) => state === 'unassigned');
    const myBookJournal = validJournals.find(
      ({ state }) => state === 'initialized' || state === 'assigned'
    );
    return unassignedJournal
      ? '/account/unassigned'
      : myBookJournal
        ? '/account/my-books'
        : '/account/gifts';
  };

  // ✅ Dispatch auth when data is available
  useEffect(() => {
    if (data) {
      const storeToken = store.get('auth_token');
      const { id: memberId } = data.data.data;
      const token = `Token ${storeToken}`;
      dispatch(setAuth({ token, memberId }));
    }
  }, [data, dispatch]);

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const getNewAuthToken = async () => {
    const params = Object.fromEntries(
      new URLSearchParams(window.location.search)
    );
    if (params?.restoreTokenId && params?.restoreTokenSecret) {
      const { restoreTokenId, restoreTokenSecret } = params;
      const res = await getAuthToken({
        id: restoreTokenId,
        secret: restoreTokenSecret,
      });
      if (res) {
        store.set('auth_token', res);
        revalidate();
      }
    }
  };

  async function createNewUserRequest() {
    if (hasRestoreToken()) {
      await getNewAuthToken();
    } else {
      await createNewUser();
      revalidate();
    }
  }

  async function getPatchDetails(orderId, retryCount = 0) {
    try {
      let customerEmail = Cookies.get('customer_email');
      let firstName = Cookies.get('first_name');
      let lastName = Cookies.get('last_name');

      setIsProcessing(true);
      setHasError(false);

      const patchResponse = await patch(
        `/v3/shopify_orders/${orderId}/auth_token`
      );

      if (
        patchResponse?.status === 201 ||
        patchResponse?.status === 200
      ) {
        store.set(
          'auth_token',
          patchResponse?.data?.initial?.data?.attributes?.['auth-token']
        );
        revalidate();

        // if (!customerEmail) {
        const { data: shopifyOrdersData } = await get(
          `/v3/shopify_orders/${orderId}`
        );
        customerEmail =
          shopifyOrdersData?.initial?.data?.attributes?.[
          'customer-email'
          ];
        const orderQuantity =
          shopifyOrdersData?.initial?.data?.attributes?.[
          'quantity'
          ];
        const order_id = shopifyOrdersData?.initial?.data?.id;
        // }

        setIsProcessing(false);
        setHasError(false);
        push(
          `/shopify-onboarding?customerEmail=${encodeURIComponent(customerEmail)}${firstName ? `&firstName=${encodeURIComponent(firstName)}` : ''}${lastName ? `&lastName=${encodeURIComponent(lastName)}` : ''}&orderQuantity=${orderQuantity}&orderId=${encodeURIComponent(order_id)}&shopifyOrderId=${encodeURIComponent(orderId)}`
        );
        return;
      } else if (
        isPublicRoute &&
        !store.get('auth_token') &&
        !pathname.includes('/password')
      ) {
        createNewUserRequest();
      }
    } catch (err) {
      console.log('Error in getPatchDetails:', err);
      if (err.message.includes('410') && retryCount < 3) {
        await sleep(3000);
        return getPatchDetails(orderId, retryCount + 1);
      }
      if (err.message.includes('410')) {
        setIsProcessing(false);
        setHasError(true);
      } else {
        push(`/`);
        setIsProcessing(false);
        // setHasError('general');
      }
    }
  }

  // Guards against re-firing getPatchDetails on every subsequent member update —
  // getPatchDetails itself calls revalidate(), which would otherwise re-trigger
  // this effect the moment the refetched member lands.
  const hasHandledUnboardedOrderRef = useRef(false);

  // Set by the bootstrap effect below when the user landed with an orderId but
  // was already logged in. The redirect itself waits for a second effect,
  // because journals/giftCertificates are still empty (SWR hasn't resolved yet)
  // at the moment the bootstrap effect runs on mount.
  const isLoggedInWithOrderRef = useRef(false);
  const hasRedirectedLoggedInOrderRef = useRef(false);

  useEffect(() => {
    const orderId = getOrderId();
    if (orderId && !Cookies.get('order_id')) {
      Cookies.set('order_id', orderId, { expires: 10 / (24 * 60) });
    }

    // Wait for the member object to actually load before deciding anything —
    // `isMemberLoaded` is what "we got it" means; `member?.['has-username-password-identifier']` isn't
    // trustworthy until then (see the isPresent note above).
    if (!orderId || !isMemberLoaded || hasHandledUnboardedOrderRef.current) return;

    if (member?.['has-username-password-identifier'] === false) {
      hasHandledUnboardedOrderRef.current = true;
      getPatchDetails(orderId);
    }
  }, [isMemberLoaded, member?.['has-username-password-identifier']])

  // ✅ Immediately bootstrap token for tell-a-story
  // Runs synchronously on first render — no dependency delay
  useEffect(() => {
    if (hasBootstrapped.current) return;
    hasBootstrapped.current = true;

    const orderId = getOrderId();

    // ✅ Priority 1: tell_a_story route — create token immediately
    // No waiting for isPublicRoute, pathname deps, or currentMemberId
    if (isTellAStoryRoute()) {
      if (!store.get('auth_token')) {
        createNewUserRequest();
      } else {
        // Token exists, just revalidate to hydrate member
        revalidate();
      }
      return;
    }

    // ✅ Priority 2: Shopify order flow
    if (orderId && !store.get('auth_token')) {
      getPatchDetails(orderId);
      return;
    }

    if (orderId && store.get('auth_token')) {
      isLoggedInWithOrderRef.current = true;
      return;
    }

    // ✅ Priority 3: General public route
    if (
      isPublicRoute &&
      !store.get('auth_token') &&
      !pathname.includes('/password')
    ) {
      createNewUserRequest();
      return;
    }

    // ✅ Priority 4: Has token but no member hydrated yet
    if (isPublicRoute && store.get('auth_token') && !currentMemberId) {
      revalidate();
    }
    // ✅ Empty deps — runs once immediately on mount, no delay from dep changes
  }, []);

  // Fires the redirect flagged above, once journals/giftCertificates have
  // actually loaded — picking the same tab MeDropdown would (unassigned book >
  // my books > gifts).
  useEffect(() => {
    if (!isLoggedInWithOrderRef.current || hasRedirectedLoggedInOrderRef.current) return;
    if (isJournalsLoading || isGiftCertificatesLoading) return;

    hasRedirectedLoggedInOrderRef.current = true;
    push(getAccountRedirect());
  }, [isJournalsLoading, isGiftCertificatesLoading]);

  function sendEmail() {
    const title = ``;
    const mailto = `mailto:help@babyyearbooks.com?subject=${encodeURIComponent(title)}`;
    window.open(mailto, '_blank');
  }

  if (isTellAStoryRoute())
    return children;

  if (isProcessing) {
    return <ProcessingScreen text="Just a moment, setting things up" />;
  }

  if (hasError === 'general') {
    return (
      <ErrorScreen
        title="An unexpected error occurred"
        description="We couldn’t process your order. Please sign in to your account to check if this order has already been attached to your account. If you still can’t see it, please contact us for assistance."
        onRetry={() => {
          push('/')
          window.location.reload()
        }}
        retryButtonText="Sign in"
      />
    );
  }

  if (hasError) {
    return (
      <ErrorScreen
        title="Something went wrong"
        description="We couldn't process your order. Please refresh or try again later."
        onRetry={sendEmail}
      />
    );
  }

  if ((error || member?.email === null) && !isPublicRoute)
    return (
      <Flex flexDirection="column">
        <LoginSignUp />
      </Flex>
    );

  if (pathname.includes('/password')) return false;

  if (!currentMemberId) return <Loading />;

  if (!isPublicRoute) {
    // if (!member['first-name'] || !member['last-name'])
    //   return <CompleteProfile />;
    if (member?.['has-username-password-identifier'] === false) return <MemberOnboardLoading />;
  }

  if (currentMemberId) return children;

  return false;
};

AuthMember.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AuthMember;